import React, { FC, useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { NumberParam, StringParam, useQueryParam } from 'use-query-params'
import { Button, SearchComponent, Select, StartPage } from '../../components'
import { dedicatedServersService } from '../../api'
import { useUi } from '../../context/ui.context'
import { usePageTitle } from '../../context/navigationContext'
import useUpdateEffect from '../../hooks/useUpdateEffect'
import { extractErrorMessage } from '../../utils/error-extractor'
import { formatDate } from '../../utils/date-formatter'
import { DedicatedServer, PaginatedDedicatedServers } from './types'
import { formatHardwareSize } from './hardware'
import {
  FilterActions,
  Filters,
  FilterRow,
  FilterSelect,
  ListPage,
  SearchField,
  SearchRow,
  Status,
  StatusDot,
} from './styles'

const getExternalUserName = (server: DedicatedServer) => {
  const user = server.external_user
  if (user) {
    const display = user.name || user.id
    if (display !== undefined && display !== null) return String(display)
  }
  return server.dedicated_user?.external_id ? `#${server.dedicated_user.external_id}` : '-'
}

const DedicatedServers: FC = () => {
  const [t] = useTranslation()
  const { viewAlert } = useUi()
  const [servers, setServers] = useState<DedicatedServer[]>([])
  const [paginationData, setPaginationData] = useState<PaginatedDedicatedServers | null>(null)
  const [pageLoading, setPageLoading] = useState(true)
  const [syncing, setSyncing] = useState(false)

  const [page, setPage] = useQueryParam('page', NumberParam)
  const [pageSize] = useQueryParam('page_size', NumberParam)
  const [ordering, setOrdering] = useQueryParam('ordering', StringParam)
  const [search, setSearch] = useQueryParam('search', StringParam)
  const [powerStatus, setPowerStatus] = useQueryParam('power_status', StringParam)
  const [isAssigned, setIsAssigned] = useQueryParam('is_assigned', StringParam)
  const [deleted, setDeleted] = useQueryParam('deleted', StringParam)

  usePageTitle(t('DEDICATED_SERVERS'))

  const updateServers = useCallback(() => {
    setPageLoading(true)
    dedicatedServersService.getServers({
      page: page || 1,
      page_size: pageSize || 10,
      ordering: ordering || undefined,
      search: search || undefined,
      power_status: powerStatus || undefined,
      is_assigned: isAssigned || undefined,
      deleted: deleted || undefined,
    })
      .then((response) => {
        setServers(response.data.objects)
        setPaginationData(response.data)
      })
      .catch((error) => {
        viewAlert({
          severity: 'error',
          message: error?.response?.data?.detail
            || extractErrorMessage(error?.response?.data)
            || t('FAILED_TO_LOAD_DEDICATED_SERVERS'),
        })
      })
      .finally(() => setPageLoading(false))
  }, [deleted, isAssigned, ordering, page, pageSize, powerStatus, search, t, viewAlert])

  useEffect(() => {
    updateServers()
  }, [updateServers])

  useUpdateEffect(() => {
    setPage(1)
  }, [pageSize, search, powerStatus, isAssigned, deleted])

  const statusOptions = useMemo(() => [
    { id: '', value: '', label: t('ALL') },
    { id: 'online', value: 'online', label: t('DEDICATED_ONLINE') },
    { id: 'offline', value: 'offline', label: t('DEDICATED_OFFLINE') },
    { id: 'rebooting', value: 'rebooting', label: t('DEDICATED_REBOOTING') },
    { id: 'unknown', value: 'unknown', label: t('DEDICATED_UNKNOWN') },
  ], [t])

  const assignmentOptions = useMemo(() => [
    { id: '', value: '', label: t('ALL') },
    { id: 'true', value: 'true', label: t('DEDICATED_ASSIGNED') },
    { id: 'false', value: 'false', label: t('DEDICATED_UNASSIGNED') },
  ], [t])

  const deletedOptions = useMemo(() => [
    { id: '', value: '', label: t('ALL') },
    { id: 'false', value: 'false', label: t('DEDICATED_ACTIVE_RECORDS') },
    { id: 'true', value: 'true', label: t('DEDICATED_DELETED_RECORDS') },
  ], [t])

  const statusLabel = useCallback((status: string) => {
    const labels: Record<string, string> = {
      online: t('DEDICATED_ONLINE'),
      offline: t('DEDICATED_OFFLINE'),
      rebooting: t('DEDICATED_REBOOTING'),
      unknown: t('DEDICATED_UNKNOWN'),
    }
    return labels[status] || status || '-'
  }, [t])

  const columns = useMemo(() => [
    { title: t('NAME'), key: 'name' },
    { title: t('DEDICATED_HOSTNAME'), key: 'hostname', sorted: true },
    { title: t('DEDICATED_POWER_STATUS'), key: 'power_status', sorted: true },
    { title: t('DEDICATED_SERVER_TYPE'), key: 'server_type', sorted: true },
    { title: t('DEDICATED_CONFIGURATION'), key: 'configuration' },
    { title: t('DEDICATED_USER'), key: 'assigned_user' },
    { title: t('DEDICATED_PRIMARY_IP'), key: 'primary_ip' },
    { title: t('DEDICATED_LAST_SEEN'), key: 'last_seen_at', sorted: true },
  ], [t])

  const rows = useMemo(() => {
    return servers.map((server) => {
      const primaryIp = server.ips?.find((ip) => ip.is_primary) || server.ips?.[0]
      const { cpu, ram, disk } = server.hardware || {}
      return {
        ...server,
        name: server.name || server.servername || server.hostname || `#${server.external_id}`,
        hostname: server.hostname || '-',
        power_status: (
          <Status status={server.power_status}>
            <StatusDot status={server.power_status} />
            {statusLabel(server.power_status)}
          </Status>
        ),
        server_type: server.server_type || '-',
        configuration: (
          <span style={{ display: 'inline-flex', flexDirection: 'column' }}>
            <span>CPU: {cpu ? `${cpu.count ?? '-'} × ${cpu.model || '-'}` : '-'}</span>
            <span>{t('RAM')}: {formatHardwareSize(ram?.value, ram?.value_type)}</span>
            <span>{t('DISK')}: {formatHardwareSize(disk?.value, disk?.value_type)} {disk?.disk_type}</span>
          </span>
        ),
        assigned_user: getExternalUserName(server),
        primary_ip: primaryIp?.ip || '-',
        last_seen_at: formatDate(server.last_seen_at),
        navigate: `/dedicated-servers/servers/${server.id}`,
      }
    })
  }, [servers, statusLabel, t])

  const syncAll = () => {
    setSyncing(true)
    dedicatedServersService.syncAllServers()
      .then(() => {
        viewAlert({ severity: 'information', message: t('DEDICATED_SYNC_ALL_STARTED') })
        updateServers()
      })
      .catch((error) => {
        viewAlert({
          severity: 'error',
          message: error?.response?.data?.detail
            || extractErrorMessage(error?.response?.data)
            || t('DEDICATED_SYNC_FAILED'),
        })
      })
      .finally(() => setSyncing(false))
  }

  const clearFilters = () => {
    setSearch(undefined)
    setPowerStatus(undefined)
    setIsAssigned(undefined)
    setDeleted(undefined)
    setOrdering(undefined)
    setPage(1)
  }

  const hasFilters = Boolean(search || powerStatus || isAssigned || deleted || ordering)

  return (
    <ListPage>
      <StartPage
      title={String(t('DEDICATED_SERVERS'))}
      noResourceText={String(t('NO_DEDICATED_SERVERS'))}
      haveResource={servers.length > 0}
      pageLoading={pageLoading}
      withoutCreateResourceButton
      rightCustomCreateButton={(
        <Filters>
          <SearchRow>
            <SearchField>
              <SearchComponent />
            </SearchField>
            <FilterActions style={{ gap: 12 }}>
              <Button
                type="button"
                title={t('DEDICATED_SYNC_ALL')}
                onClick={syncAll}
                variant="primary"
                size="display"
                loading={syncing}
              />
              <Button
                title={t('CLEAR_FILTERS')}
                onClick={clearFilters}
                variant="stroke"
                size="display"
                disabled={!hasFilters}
              />
            </FilterActions>
          </SearchRow>
          <FilterRow>
            <FilterSelect>
              <Select
                title={t('DEDICATED_POWER_STATUS')}
                data={statusOptions}
                selectedValue={powerStatus || ''}
                onChange={(value) => setPowerStatus(value || undefined)}
              />
            </FilterSelect>
            <FilterSelect>
              <Select
                title={t('DEDICATED_ASSIGNMENT')}
                data={assignmentOptions}
                selectedValue={isAssigned || ''}
                onChange={(value) => setIsAssigned(value || undefined)}
              />
            </FilterSelect>
            <FilterSelect>
              <Select
                title={t('DEDICATED_RECORD_STATE')}
                data={deletedOptions}
                selectedValue={deleted || ''}
                onChange={(value) => setDeleted(value || undefined)}
              />
            </FilterSelect>
          </FilterRow>
        </Filters>
      )}
      tableData={{
        columns,
        data: rows,
        paginationData,
        updateData: updateServers,
        hideCheckbox: true,
      }}
      />
    </ListPage>
  )
}

export default DedicatedServers
